/**
 * /me/profile — employee self-service profile (read + narrow edit).
 *
 * UI choice: dedicated route (not inline-edit on /me). Reason: ~15
 * read-only fields across 6 sections fit a profile page better than
 * a dashboard tile, and the read-only-by-default → Edit → Cancel/Save
 * pattern reads cleaner with its own surface.
 *
 * Editable surface (kept narrow on purpose): phone, personalEmail,
 * homeAddress, nextOfKin. Everything else is HR-managed; the server's
 * UpdateMyProfileInput is .strict() so a smuggled field will 400 even
 * if the UI bug let it through.
 */
import { createRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  type CountryCode,
  type MeProfileResponse,
  type NextOfKin,
  UpdateMyProfileInput,
} from '@kora/types';
import { meLayoutRoute } from './meLayout.js';
import { getMeProfile, updateMyProfile } from '../api/me.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';

export const myProfileRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: '/me/profile',
  component: MyProfilePage,
});

function MyProfilePage(): JSX.Element {
  const profileQuery = useQuery({
    queryKey: ['me', 'profile'],
    queryFn: getMeProfile,
  });

  if (profileQuery.isLoading) {
    return (
      <section className="p-8 max-w-3xl">
        <div className="rounded border border-gray-900 p-8 text-center text-gray-500">
          Loading…
        </div>
      </section>
    );
  }
  if (profileQuery.isError || !profileQuery.data) {
    return (
      <section className="p-8 max-w-3xl">
        <Alert variant="error">
          Could not load your profile. Try refreshing — if the problem
          persists, contact HR.
        </Alert>
      </section>
    );
  }

  return <ProfileView profile={profileQuery.data} />;
}

// ---------------------------------------------------------------------------
// View / Edit shell
// ---------------------------------------------------------------------------

function ProfileView({ profile }: { profile: MeProfileResponse }): JSX.Element {
  const [editing, setEditing] = useState(false);
  const { employee } = profile;

  return (
    <section className="p-8 space-y-6 max-w-3xl">
      <header className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <Link
              to="/me"
              className="text-xs text-gray-500 hover:text-white underline-offset-2 hover:underline"
            >
              ← Back to dashboard
            </Link>
            <h1 className="font-serif text-3xl">My profile</h1>
          </div>
          {!editing ? (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="rounded border border-gray-700 px-4 py-2 text-sm hover:bg-gray-900"
            >
              Edit
            </button>
          ) : null}
        </div>
        <p className="text-sm text-gray-400">
          This is the record HR has on file. You can update contact details
          and your emergency contact directly. For other corrections,
          contact HR.
        </p>
      </header>

      {editing ? (
        <ProfileEditForm
          employee={employee}
          onDone={() => setEditing(false)}
        />
      ) : (
        <ProfileReadOnly employee={employee} />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Read-only view — six sections
// ---------------------------------------------------------------------------

function ProfileReadOnly({
  employee,
}: {
  employee: MeProfileResponse['employee'];
}): JSX.Element {
  return (
    <div className="space-y-6">
      <Section title="Identity">
        <Row label="First name" value={employee.firstName} />
        <Row label="Middle name" value={employee.middleName ?? '—'} />
        <Row label="Last name" value={employee.lastName} />
        <Row label="Gender" value={formatGender(employee.gender)} />
        <Row label="Date of birth" value={formatDateOrDash(employee.dateOfBirth)} />
      </Section>

      <Section title="Employment">
        <Row label="Employee number" value={employee.employeeNumber} mono />
        <Row label="Country" value={employee.countryCode} />
        <Row label="State / region" value={employee.stateCode ?? '—'} />
        <Row label="Hire date" value={formatIsoDate(employee.hireDate)} />
        <Row label="Position" value={employee.position ?? '—'} />
        <Row label="Department" value={employee.department ?? '—'} />
        <Row label="Work arrangement" value={formatEmploymentType(employee.employmentType)} />
        <Row
          label="Manager"
          value={
            employee.manager
              ? `${employee.manager.firstName} ${employee.manager.lastName}`
              : 'No manager assigned'
          }
        />
        <Row label="Work location" value={employee.workLocation ?? '—'} />
        <Row label="Status" value={employee.status.replace('_', ' ')} />
      </Section>

      <Section title="Contact">
        <Row label="Work email" value={employee.email ?? '—'} />
        <Row label="Personal email" value={employee.personalEmail ?? '—'} />
        <Row label="Phone" value={employee.phone ?? '—'} mono />
        <Row label="Home address" value={employee.homeAddress ?? '—'} />
      </Section>

      <Section title="Statutory IDs">
        <Row label="Tax identifier (TIN)" value={employee.taxIdentifier ?? '—'} mono />
        <Row
          label={socialSecurityLabel(employee.countryCode)}
          value={employee.socialSecurityId ?? '—'}
          mono
        />
      </Section>

      <Section title="Banking">
        <Row label="SWIFT / BIC" value={employee.bankSwiftCode ?? '—'} mono />
        {employee.bankName ? <Row label="Bank" value={employee.bankName} /> : null}
        {employee.bankAccountNumber ? (
          <Row label="Account No." value={employee.bankAccountNumber} mono />
        ) : null}
        {employee.bankAccountName ? (
          <Row label="Account Name" value={employee.bankAccountName} />
        ) : null}
        <p className="px-4 py-2 text-xs text-gray-500">
          To update your bank details, complete the bank update form and submit it to HR.
        </p>
      </Section>

      <Section title="Next of kin (emergency contact)">
        {employee.nextOfKin ? (
          <>
            <Row label="Name" value={employee.nextOfKin.name} />
            <Row label="Relationship" value={employee.nextOfKin.relationship} />
            <Row label="Phone" value={employee.nextOfKin.phoneNumber} mono />
            <Row label="Email" value={employee.nextOfKin.email ?? '—'} />
            <Row label="Address" value={employee.nextOfKin.address ?? '—'} />
          </>
        ) : (
          <div className="px-4 py-4 text-sm text-gray-500">
            No emergency contact on file. Click Edit to add one.
          </div>
        )}
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit form — editable fields only; read-only fields still shown above
// ---------------------------------------------------------------------------

interface ProfileFormValues {
  phone: string;
  personalEmail: string;
  homeAddress: string;
  nextOfKin: {
    name: string;
    relationship: string;
    phoneNumber: string;
    email: string;
    address: string;
  };
}

function valuesFromEmployee(
  employee: MeProfileResponse['employee'],
): ProfileFormValues {
  const nk = employee.nextOfKin;
  return {
    phone: employee.phone ?? '',
    personalEmail: employee.personalEmail ?? '',
    homeAddress: employee.homeAddress ?? '',
    nextOfKin: {
      name: nk?.name ?? '',
      relationship: nk?.relationship ?? '',
      phoneNumber: nk?.phoneNumber ?? '',
      email: nk?.email ?? '',
      address: nk?.address ?? '',
    },
  };
}

function ProfileEditForm({
  employee,
  onDone,
}: {
  employee: MeProfileResponse['employee'];
  onDone: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);
  const defaults = valuesFromEmployee(employee);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ProfileFormValues>({
    // Validate via the same Zod schema the server uses, with one
    // adapter step to convert the flat form shape into the
    // UpdateMyProfileInput shape (NextOfKin gathered into an object).
    resolver: zodResolver(ProfileFormSchema),
    defaultValues: defaults,
  });

  useEffect(() => {
    // Re-seed the form when the loaded employee record changes — e.g.
    // after a successful save invalidates the cache and a fresh row
    // arrives. RHF needs an explicit reset to update displayed values.
    // Defaults is recomputed on every render, so it's intentionally
    // omitted from deps; identity-comparing `employee` is the signal.
    reset(defaults);
  }, [employee, reset]);

  const mutation = useMutation({
    mutationFn: updateMyProfile,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me', 'profile'] });
      onDone();
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        if (err.status === 400) {
          setServerError(
            'Some of your changes were invalid. Check the highlighted fields.',
          );
          return;
        }
        if (err.status === 401 || err.status === 403) {
          setServerError('Your session expired. Sign in again.');
          return;
        }
      }
      setServerError('Could not save your profile. Try again or contact HR.');
    },
  });

  async function onSubmit(values: ProfileFormValues): Promise<void> {
    setServerError(null);
    const body = buildPatchBody(values, defaults);
    if (Object.keys(body).length === 0) {
      // Nothing changed — skip the round-trip + audit noise. Treat as
      // a successful save UX-wise; just exit edit mode.
      onDone();
      return;
    }
    mutation.mutate(body);
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
      {/* Read-only sections still visible — operator sees the full
          record while editing, just like the admin edit view. */}
      <Section title="Identity">
        <Row label="First name" value={employee.firstName} />
        <Row label="Middle name" value={employee.middleName ?? '—'} />
        <Row label="Last name" value={employee.lastName} />
        <Row label="Gender" value={formatGender(employee.gender)} />
        <Row label="Date of birth" value={formatDateOrDash(employee.dateOfBirth)} />
      </Section>

      <Section title="Employment">
        <Row label="Employee number" value={employee.employeeNumber} mono />
        <Row label="Country" value={employee.countryCode} />
        <Row label="State / region" value={employee.stateCode ?? '—'} />
        <Row label="Hire date" value={formatIsoDate(employee.hireDate)} />
        <Row label="Position" value={employee.position ?? '—'} />
        <Row label="Department" value={employee.department ?? '—'} />
        <Row label="Work arrangement" value={formatEmploymentType(employee.employmentType)} />
        <Row
          label="Manager"
          value={
            employee.manager
              ? `${employee.manager.firstName} ${employee.manager.lastName}`
              : 'No manager assigned'
          }
        />
        <Row label="Work location" value={employee.workLocation ?? '—'} />
        <Row label="Status" value={employee.status.replace('_', ' ')} />
      </Section>

      <SectionEditable title="Contact">
        <Row label="Work email" value={employee.email ?? '—'} />
        <Field
          id="personalEmail"
          label="Personal email"
          type="email"
          placeholder="you@personal.com"
          error={errors.personalEmail?.message}
          inputProps={register('personalEmail')}
        />
        <Field
          id="phone"
          label="Phone"
          type="tel"
          placeholder="+234 803 …"
          error={errors.phone?.message}
          inputProps={register('phone')}
        />
        <Field
          id="homeAddress"
          label="Home address"
          placeholder="Street, city, postcode"
          error={errors.homeAddress?.message}
          inputProps={register('homeAddress')}
        />
      </SectionEditable>

      <Section title="Statutory IDs">
        <Row label="Tax identifier (TIN)" value={employee.taxIdentifier ?? '—'} mono />
        <Row
          label={socialSecurityLabel(employee.countryCode)}
          value={employee.socialSecurityId ?? '—'}
          mono
        />
      </Section>

      <Section title="Banking">
        <Row label="SWIFT / BIC" value={employee.bankSwiftCode ?? '—'} mono />
        {employee.bankName ? <Row label="Bank" value={employee.bankName} /> : null}
        {employee.bankAccountNumber ? (
          <Row label="Account No." value={employee.bankAccountNumber} mono />
        ) : null}
        {employee.bankAccountName ? (
          <Row label="Account Name" value={employee.bankAccountName} />
        ) : null}
        <p className="px-4 py-2 text-xs text-gray-500">
          To update your bank details, complete the bank update form and submit it to HR.
        </p>
      </Section>

      <SectionEditable title="Next of kin (emergency contact)">
        <p className="px-4 pt-3 text-xs text-gray-500">
          Clear all five fields to remove the contact entirely.
        </p>
        <Field
          id="nextOfKin.name"
          label="Name"
          error={errors.nextOfKin?.name?.message}
          inputProps={register('nextOfKin.name')}
        />
        <Field
          id="nextOfKin.relationship"
          label="Relationship"
          placeholder="Spouse, parent, sibling…"
          error={errors.nextOfKin?.relationship?.message}
          inputProps={register('nextOfKin.relationship')}
        />
        <Field
          id="nextOfKin.phoneNumber"
          label="Phone"
          type="tel"
          error={errors.nextOfKin?.phoneNumber?.message}
          inputProps={register('nextOfKin.phoneNumber')}
        />
        <Field
          id="nextOfKin.email"
          label="Email (optional)"
          type="email"
          error={errors.nextOfKin?.email?.message}
          inputProps={register('nextOfKin.email')}
        />
        <Field
          id="nextOfKin.address"
          label="Address (optional)"
          error={errors.nextOfKin?.address?.message}
          inputProps={register('nextOfKin.address')}
        />
      </SectionEditable>

      {serverError ? (
        <Alert variant="error">{serverError}</Alert>
      ) : null}

      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={() => {
            reset(defaults);
            setServerError(null);
            onDone();
          }}
          className="rounded border border-gray-800 px-4 py-2 text-sm text-gray-300 hover:bg-gray-900"
          disabled={isSubmitting || mutation.isPending}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="rounded bg-white text-black px-4 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isSubmitting || mutation.isPending}
        >
          {mutation.isPending ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Schema adapter + diff
// ---------------------------------------------------------------------------

/**
 * Form-only Zod schema. Empty strings are valid here (they mean "clear
 * this field" and are converted to `null` in `buildPatchBody` before
 * the request leaves the browser). The server-side UpdateMyProfileInput
 * is .strict() and doesn't accept empty strings — that's the contract
 * boundary, this is just for inline form feedback.
 */
const ProfileFormSchema = z.object({
  phone: z.union([z.literal(''), z.string().min(4).max(30)]),
  personalEmail: z.union([z.literal(''), z.string().email().max(255)]),
  homeAddress: z.union([z.literal(''), z.string().min(1).max(500)]),
  nextOfKin: z.object({
    name: z.union([z.literal(''), z.string().min(1).max(100)]),
    relationship: z.union([z.literal(''), z.string().min(1).max(50)]),
    phoneNumber: z.union([z.literal(''), z.string().min(4).max(30)]),
    email: z.union([z.literal(''), z.string().email().max(255)]),
    address: z.union([z.literal(''), z.string().max(500)]),
  }),
});

/**
 * Build the PATCH body containing only fields the user actually
 * changed. Skipping unchanged keys keeps the audit log clean (the
 * server writes an audit event for any non-empty body, even if the
 * after value matches the before value — see me.ts PATCH handler).
 *
 * Empty strings become null (clear). NextOfKin is collapsed: if all
 * sub-fields are empty AND the original had a contact, emit
 * `nextOfKin: null`; if the user filled the required sub-fields,
 * emit a NextOfKin object (optional email/address omitted when empty
 * to match the NextOfKin schema).
 */
function buildPatchBody(
  values: ProfileFormValues,
  defaults: ProfileFormValues,
): UpdateMyProfileInput {
  const body: UpdateMyProfileInput = {};

  if (values.phone !== defaults.phone) {
    body.phone = values.phone === '' ? null : values.phone;
  }
  if (values.personalEmail !== defaults.personalEmail) {
    body.personalEmail = values.personalEmail === '' ? null : values.personalEmail;
  }
  if (values.homeAddress !== defaults.homeAddress) {
    body.homeAddress = values.homeAddress === '' ? null : values.homeAddress;
  }

  // NextOfKin diff: any sub-field changed → re-emit the whole block
  // (server replaces the column wholesale, no partial-merge semantics).
  const kinChanged =
    values.nextOfKin.name !== defaults.nextOfKin.name ||
    values.nextOfKin.relationship !== defaults.nextOfKin.relationship ||
    values.nextOfKin.phoneNumber !== defaults.nextOfKin.phoneNumber ||
    values.nextOfKin.email !== defaults.nextOfKin.email ||
    values.nextOfKin.address !== defaults.nextOfKin.address;

  if (kinChanged) {
    const v = values.nextOfKin;
    const allEmpty =
      !v.name && !v.relationship && !v.phoneNumber && !v.email && !v.address;
    if (allEmpty) {
      body.nextOfKin = null;
    } else {
      const kin: NextOfKin = {
        name: v.name,
        relationship: v.relationship,
        phoneNumber: v.phoneNumber,
      };
      if (v.email) kin.email = v.email;
      if (v.address) kin.address = v.address;
      body.nextOfKin = kin;
    }
  }

  // Final defensive validation — should always pass after the form
  // adapter + transformation, but a strict parse here guarantees the
  // body conforms to the server contract (catches the case where the
  // adapter and UpdateMyProfileInput drift apart in a future refactor).
  return UpdateMyProfileInput.parse(body);
}

// ---------------------------------------------------------------------------
// Display primitives — match the admin Edit Employee visual language
// ---------------------------------------------------------------------------

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="rounded border border-gray-900 divide-y divide-gray-900">
      <header className="px-4 py-3 text-xs uppercase tracking-wide text-gray-400">
        {title}
      </header>
      {children}
    </div>
  );
}

function SectionEditable({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="rounded border border-gray-900 divide-y divide-gray-900">
      <header className="px-4 py-3 text-xs uppercase tracking-wide text-gray-400">
        {title}
      </header>
      <div className="p-4 space-y-4">{children}</div>
    </div>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-4 px-4 py-3 text-sm">
      <span className="text-gray-500 uppercase tracking-wide text-xs shrink-0">
        {label}
      </span>
      <span className={`text-right ${mono ? 'font-mono text-xs' : ''}`}>
        {value}
      </span>
    </div>
  );
}

interface FieldProps {
  id: string;
  label: string;
  type?: string;
  placeholder?: string;
  error?: string;
  inputProps: ReturnType<ReturnType<typeof useForm<ProfileFormValues>>['register']>;
}

function Field({
  id,
  label,
  type = 'text',
  placeholder,
  error,
  inputProps,
}: FieldProps): JSX.Element {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs uppercase tracking-wide text-gray-400">
        {label}
      </label>
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        aria-invalid={error ? 'true' : 'false'}
        className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white placeholder-gray-600 focus:border-white focus:outline-none"
        {...inputProps}
      />
      {error ? <p className="text-xs text-red-400">{error}</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Format helpers
// ---------------------------------------------------------------------------

function socialSecurityLabel(country: CountryCode): string {
  switch (country) {
    case 'NG':
      return 'PenCom PIN';
    case 'GH':
      return 'SSNIT Number';
    case 'CD':
      return 'CNSS Number';
    default:
      return 'Social Security ID';
  }
}

function formatGender(g: MeProfileResponse['employee']['gender']): string {
  if (!g) return '—';
  return g
    .replace('_', ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatEmploymentType(
  t: MeProfileResponse['employee']['employmentType'],
): string {
  if (!t) return '—';
  return t.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatIsoDate(iso: string): string {
  // hireDate comes through as full ISO; show as YYYY-MM-DD.
  return iso.slice(0, 10);
}

function formatDateOrDash(d: string | null): string {
  return d ?? '—';
}
